import React, { useState, useEffect } from "react";
import { Input, Avatar, Modal, Button, message, Tooltip } from "antd";
import UserInitialsAvatar from "./UserInitialsAvatar";
import { useAlert } from "../../../context/AlertProvider";
import {
  SearchOutlined,
  UserOutlined,
  DownOutlined,
  UpOutlined,
  PhoneOutlined,
  PlusCircleOutlined,
  CheckCircleFilled,
  PlusOutlined,
} from "@ant-design/icons";
import moment from "moment";
import WhatsAppAccountHealthBadge, { WhatsAppQualityBadge } from "../WhatsAppAccountHealthBadge";

const ChatListSidebar = ({
  chats,
  activeChatId,
  onSelectChat,
  onNewChat,
  connectionData,
  selectedPhoneId,
  selectedWabaId,
  onSelectPhone,
  loadMoreChats,
  hasMoreChats,
  onSearchChange,
}) => {
  const { showAlert } = useAlert();
  const [activeFilter, setActiveFilter] = useState("All Chats");

  const filterTooltips = {
    "All Chats": "View all conversations",
    Unread: "View unread conversations",
    Archived: "View archived conversations",
  };
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [newAccountName, setNewAccountName] = useState("");
  const [newAccountPhone, setNewAccountPhone] = useState("");
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);
  const [currentTime, setCurrentTime] = useState(moment());
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(moment()), 60000);
    return () => clearInterval(timer);
  }, []);

  const businessName =
    connectionData?.businessName || "WhatsApp Business Account";

  // Map real phone numbers from API to the format used by the UI
  const accounts = (connectionData?.phoneNumbers || []).map((pn, index) => {
    const cleanPnPhone = String(pn.displayPhoneNumber || pn.phoneNumber || "").replace(/\D/g, "");
    const pnWabaId =
      pn.wabaId ||
      pn.whatsappBusinessAccountId ||
      connectionData?.wabaId ||
      connectionData?.id;

    const pnActiveChats = chats.filter((c) => {
      // 1. Primary check: whatsappPhoneNumberId matching pn.id
      if (c.whatsappPhoneNumberId && pn.id) {
        return String(c.whatsappPhoneNumberId) === String(pn.id);
      }
      // 2. Secondary check: phone number match
      if (cleanPnPhone && (c.businessPhoneNumber || c.phoneNumber || c.whatsappPhoneNumber)) {
        const cPhone = String(c.businessPhoneNumber || c.phoneNumber || c.whatsappPhoneNumber).replace(/\D/g, "");
        if (cPhone && cPhone === cleanPnPhone) return true;
      }
      // 3. If pn has distinct wabaId
      if (pnWabaId && c.wabaId !== undefined && c.wabaId !== null) {
        return String(c.wabaId) === String(pnWabaId);
      }
      return (connectionData?.phoneNumbers?.length || 0) <= 1;
    }).length;

    return {
      id: pn.id,
      wabaId: pnWabaId,
      name: businessName,
      phone: pn.displayPhoneNumber,
      rawQuality: pn.qualityRating || pn.quotaSnapshot?.quality || 'RED',
      activeChats: pnActiveChats,
      avatarText:
        businessName
          .split(" ")
          .map((w) => w[0])
          .join("")
          .substring(0, 2)
          .toUpperCase() || "WA",
      avatarColor: ["#85E185", "#B1A3FF", "#92BAFF", "#86D2D9", "#F59E0B"][
        index % 5
      ],
      status:
        pn.cloudApiRegistrationStatus === "REGISTERED" ? "Connected" : "Pending",
      unreadCount: 0,
    };
  });

  const activeAccount = accounts.find((acc) => String(acc.id) === String(selectedPhoneId)) ||
    accounts[0] || {
      id: "dummy",
      wabaId: selectedWabaId || 1,
      name: "Loading...",
      phone: "",
      rawQuality: "RED",
      activeChats: 0,
      avatarText: "WA",
      avatarColor: "#85E185",
      status: "Loading",
      unreadCount: 0,
    };

  const handleSelectAccount = (id, wabaId) => {
    try {
      localStorage.setItem("whatsapp_selected_phone_id", id);
      if (wabaId) {
        localStorage.setItem("whatsapp_selected_waba_id", wabaId);
      }
    } catch (e) {}
    if (onSelectPhone) {
      onSelectPhone(id, wabaId);
    }
    setIsDropdownOpen(false);
    setIsSwitchingAccount(true);
    setTimeout(() => {
      setIsSwitchingAccount(false);
    }, 600);
  };

  const handleAddNewAccount = () => {
    if (!newAccountName.trim() || !newAccountPhone.trim()) {
      showAlert("warning", "Please fill in all fields");
      return;
    }

    showAlert("success", `Connected new account: ${newAccountName}`);
    setIsConnectModalOpen(false);
    setNewAccountName("");
    setNewAccountPhone("");
  };

  const cleanSelectedPhone = String(activeAccount?.phone || "").replace(/\D/g, "");

  const phoneChats = chats.filter((chat) => {
    if (!selectedPhoneId) return true;

    // 1. Primary check: whatsappPhoneNumberId matching selectedPhoneId
    if (chat.whatsappPhoneNumberId) {
      return String(chat.whatsappPhoneNumberId) === String(selectedPhoneId);
    }

    // 2. Secondary check: phone number match
    if (cleanSelectedPhone && (chat.businessPhoneNumber || chat.phoneNumber || chat.whatsappPhoneNumber)) {
      const cPhone = String(chat.businessPhoneNumber || chat.phoneNumber || chat.whatsappPhoneNumber).replace(/\D/g, "");
      if (cPhone && cPhone === cleanSelectedPhone) return true;
    }

    // 3. If there is only 1 connected phone number, allow
    if ((connectionData?.phoneNumbers?.length || 0) <= 1) {
      return true;
    }

    return false;
  });

  const unreadChatsCount = phoneChats.filter(
    (chat) => !chat.isArchived && (chat.unreadCount > 0 || chat.unread > 0)
  ).length;

  const filteredChats = phoneChats.filter((chat) => {
    if (activeFilter === "Archived") return Boolean(chat.isArchived);
    if (activeFilter === "Unread")
      return !chat.isArchived && (chat.unreadCount > 0 || chat.unread > 0);
    return !chat.isArchived;
  });

  const getProcessedChats = () => {
    let filtered = filteredChats;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.phone?.toLowerCase().includes(q) ||
          c.lastMessage?.toLowerCase().includes(q) ||
          c.message?.toLowerCase().includes(q)
      );
    }

    const pinned = filtered.filter((c) => c.isPinned);
    const unpinned = filtered.filter((c) => !c.isPinned);
    return [...pinned, ...unpinned];
  };

  const processedChats = getProcessedChats();

  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 50;
    if (bottom && hasMoreChats) {
      if (loadMoreChats) loadMoreChats();
    }
  };

  return (
    <div className="tw-w-full xl:tw-w-[320px] xl:tw-min-w-[320px] tw-flex tw-flex-col tw-bg-white tw-rounded-xl xl:tw-rounded-none xl:tw-border-0 xl:tw-border-r xl:tw-border-border-secondary tw-overflow-visible xl:tw-self-stretch tw-relative">
      {isDropdownOpen && (
        <div
          className="tw-fixed tw-inset-0 tw-z-40"
          onClick={() => setIsDropdownOpen(false)}
        />
      )}

      <div className="tw-pt-0 tw-pb-4 tw-shrink-0 tw-relative">
        {/* Unified Account & Health Card */}
        <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/90 tw-rounded-2xl tw-p-3.5 tw-mb-3.5 tw-shadow-[0_4px_20px_rgba(0,0,0,0.03)] tw-transition-all">
          <div
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="tw-flex tw-justify-between tw-items-center tw-cursor-pointer hover:tw-opacity-95"
          >
            <div className="tw-flex tw-gap-3 tw-items-center tw-min-w-0 tw-flex-1">
              <div className="tw-relative tw-flex-shrink-0">
                <div
                  className="tw-w-10 tw-h-10 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold tw-text-sm tw-shadow-sm"
                  style={{ backgroundColor: activeAccount.avatarColor }}
                >
                  {activeAccount.avatarText}
                </div>
                <span className="tw-absolute tw-bottom-[-1px] tw-right-[-1px] tw-w-3 tw-h-3 tw-bg-[#25D366] tw-border-2 tw-border-solid tw-border-white tw-rounded-full"></span>
              </div>

              <div className="tw-flex tw-flex-col tw-min-w-0 tw-flex-1">
                <h3 className="tw-text-sm tw-font-bold tw-text-text-primary tw-m-0 tw-truncate">
                  {activeAccount.name}
                </h3>
                <div className="tw-flex tw-items-center tw-gap-1.5 tw-mt-0.5 tw-whitespace-nowrap">
                  <span className="tw-w-1.5 tw-h-1.5 tw-bg-[#25D366] tw-rounded-full tw-inline-block tw-flex-shrink-0"></span>
                  <span className="tw-text-[11px] tw-text-[#25D366] tw-font-medium">
                    {activeAccount.status}
                  </span>
                  <span className="tw-text-[11px] tw-text-sub-text tw-font-medium">
                    {activeAccount.phone}
                  </span>
                </div>
              </div>
            </div>

            <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0 tw-ml-2">
              {isDropdownOpen ? (
                <UpOutlined className="tw-text-sub-text tw-text-[12px]" />
              ) : (
                <DownOutlined className="tw-text-sub-text tw-text-[12px]" />
              )}
            </div>
          </div>

          {/* Sub-row: Active Chats & Quality Rating Badge */}
          <div className="tw-flex tw-items-center tw-justify-between tw-mt-2.5 tw-pt-2 tw-border-t tw-border-b-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100">
            <span className="tw-text-[11px] tw-text-sub-text tw-font-medium">
              {activeAccount.activeChats} active chats
            </span>
            <WhatsAppQualityBadge rating={activeAccount.rawQuality} compact={true} />
          </div>

          {/* Embedded Health Usage Bar & Actions */}
          <WhatsAppAccountHealthBadge 
            phoneNumbers={connectionData?.phoneNumbers || []} 
            activePhoneId={selectedPhoneId}
            onSelectPhone={onSelectPhone}
            isSidebar={true} 
            isEmbedded={true} 
          />
        </div>

        {isDropdownOpen && (
          <div className="tw-absolute tw-top-0 tw-left-4 tw-right-4 tw-bg-white tw-shadow-none tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/90 tw-z-50 tw-overflow-hidden">
            <div
              onClick={(e) => {
                e.stopPropagation();
                setIsDropdownOpen(false);
              }}
              className="tw-p-3.5 tw-flex tw-justify-between tw-items-center hover:tw-bg-gray-50 tw-cursor-pointer tw-transition-colors"
            >
              <div className="tw-flex tw-gap-3 tw-items-center">
                <div
                  className="tw-w-10 tw-h-10 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold tw-text-sm"
                  style={{ backgroundColor: activeAccount.avatarColor }}
                >
                  {activeAccount.avatarText}
                </div>

                <div className="tw-flex tw-flex-col">
                  <h3 className="tw-text-sm tw-font-bold tw-text-text-primary tw-m-0">
                    {activeAccount.name}
                  </h3>
                  <div className="tw-flex tw-items-center tw-gap-1.5 tw-mt-0.5 tw-whitespace-nowrap">
                    <span className="tw-w-1.5 tw-h-1.5 tw-bg-[#25D366] tw-rounded-full tw-inline-block"></span>
                    <span className="tw-text-[11px] tw-text-[#25D366] tw-font-medium">
                      {activeAccount.status}
                    </span>
                    <span className="tw-text-[11px] tw-text-sub-text tw-font-medium">
                      {activeAccount.phone}
                    </span>
                  </div>
                  <span className="tw-text-[11px] tw-text-sub-text tw-mt-0.5">
                    {activeAccount.activeChats} active chats
                  </span>
                </div>
              </div>
              <UpOutlined className="tw-text-sub-text tw-text-[12px] tw-cursor-pointer hover:tw-text-text-primary" />
            </div>

            <hr className="tw-border-gray-100 tw-my-0" />

            <div
              className="tw-p-3 tw-pt-3 tw-bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="tw-text-[11px] tw-font-semibold tw-text-gray-400 tw-mb-2 tw-px-1.5 tw-uppercase tw-tracking-wider">
                Connected WhatsApp Numbers
              </div>

              <div className="tw-max-h-[220px] tw-overflow-y-auto">
                {accounts.map((acc) => {
                  const isSelected = String(acc.id) === String(selectedPhoneId);
                  return (
                    <div
                      key={acc.id}
                      onClick={() => handleSelectAccount(acc.id, acc.wabaId)}
                      className={`tw-flex tw-items-start tw-p-3 tw-mb-2 tw-rounded-xl tw-cursor-pointer tw-transition-all tw-bg-white hover:tw-bg-slate-50 ${
                        isSelected
                          ? "tw-border tw-border-solid tw-border-emerald-500/40 tw-shadow-sm"
                          : "tw-border tw-border-solid tw-border-slate-100"
                      }`}
                    >
                      <div className="tw-flex tw-gap-3 tw-items-start tw-min-w-0 tw-w-full">
                        <div
                          className="tw-w-8 tw-h-8 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold tw-text-xs tw-flex-shrink-0 tw-mt-0.5"
                          style={{ backgroundColor: acc.avatarColor }}
                        >
                          {acc.avatarText}
                        </div>
                        <div className="tw-flex tw-flex-col tw-min-w-0 tw-flex-1">
                          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                            <span className="tw-text-xs tw-font-semibold tw-text-text-primary tw-truncate">
                              {acc.name}
                            </span>
                            {isSelected && (
                              <CheckCircleFilled className="tw-text-emerald-500 tw-text-xs tw-flex-shrink-0" />
                            )}
                          </div>

                          <div className="tw-flex tw-items-center tw-gap-1 tw-mt-0.5 tw-text-sub-text tw-whitespace-nowrap">
                            <PhoneOutlined className="tw-text-[9px] tw-flex-shrink-0" />
                            <span className="tw-text-[10px] tw-font-medium tw-whitespace-nowrap">{acc.phone}</span>
                          </div>

                          <div className="tw-mt-1.5 tw-flex tw-items-center tw-justify-between">
                            <WhatsAppQualityBadge rating={acc.rawQuality} compact={true} />
                            <span className="tw-text-[10px] tw-text-sub-text">{acc.activeChats} chats</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <hr className="tw-border-gray-100 tw-my-0" />

            <div
              onClick={(e) => {
                e.stopPropagation();
                setIsDropdownOpen(false);
                setIsConnectModalOpen(true);
              }}
              className="tw-flex tw-items-center tw-gap-2 tw-p-3.5 tw-cursor-pointer hover:tw-bg-gray-50 tw-transition-colors tw-text-[#25D366] tw-font-semibold tw-text-xs"
            >
              <PlusCircleOutlined className="tw-text-base tw-text-[#25D366]" />
              <span>Connect New WhatsApp Number</span>
            </div>
          </div>
        )}

        <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
          <h2 className="tw-text-lg tw-font-bold tw-text-text-primary tw-m-0 tw-flex tw-items-center tw-gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="tw-text-gray-500"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            Inbox
          </h2>
          <div className="tw-flex tw-items-center tw-gap-3">
            <span className="tw-text-xs tw-text-sub-text tw-font-semibold">
              {phoneChats.length} Chats
            </span>
            <Button
              type="primary"
              icon={<PlusOutlined className="tw-text-white tw-text-xs" />}
              onClick={onNewChat}
              className="!tw-bg-[#25D366] hover:!tw-bg-[#25D366] !tw-border-[#25D366] hover:!tw-border-[#25D366] !tw-text-white hover:!tw-text-white tw-shadow-none tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-text-xs md:tw-text-sm tw-font-bold tw-h-8 tw-px-3.5"
            >
              New Chat
            </Button>
          </div>
        </div>

        <Input
          prefix={<SearchOutlined className="tw-text-gray-400" />}
          placeholder="Search name, phone, or email..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (onSearchChange) onSearchChange(e.target.value);
          }}
          className="tw-h-10 tw-rounded-lg tw-border-border-secondary"
        />

        <div className="tw-flex tw-gap-2 tw-mt-4 tw-overflow-x-auto no-scrollbar tw-pb-1">
          {["All Chats", "Unread", "Archived"].map((filter) => {
            const isUnread = filter === "Unread";
            return (
              <Tooltip key={filter} title={filterTooltips[filter]}>
                <div
                  onClick={() => setActiveFilter(filter)}
                  className={`tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-whitespace-nowrap tw-cursor-pointer tw-transition-colors tw-flex tw-items-center tw-gap-1.5 ${
                    activeFilter === filter
                      ? "tw-bg-[#25D366] tw-text-white"
                      : "tw-bg-secondary-color tw-text-text-primary hover:tw-bg-gray-150"
                  }`}
                >
                  <span>{filter}</span>
                  {isUnread && unreadChatsCount > 0 && (
                    <span
                      className={`tw-px-1.5 tw-py-0.2 tw-rounded-full tw-text-[10px] tw-font-bold ${
                        activeFilter === filter
                          ? "tw-bg-white tw-text-[#25D366]"
                          : "tw-bg-[#25D366] tw-text-white"
                      }`}
                    >
                      {unreadChatsCount}
                    </span>
                  )}
                </div>
              </Tooltip>
            );
          })}
        </div>
      </div>

      <div
        className="tw-flex-1 tw-overflow-y-auto tw-px-2 tw-min-h-0 tw-max-h-[320px] xl:tw-max-h-none tw-pb-4"
        onScroll={handleScroll}
      >
        {isSwitchingAccount ? (
          <div className="tw-p-4 tw-flex tw-flex-col tw-gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="tw-flex tw-gap-3 tw-animate-pulse tw-items-center"
              >
                <div className="tw-w-[26px] tw-h-[26px] tw-bg-slate-100 tw-rounded-full tw-flex-shrink-0" />
                <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-2">
                  <div className="tw-w-24 tw-h-3 tw-bg-slate-150 tw-rounded"></div>
                  <div className="tw-w-full tw-h-2.5 tw-bg-slate-100 tw-rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : processedChats.length === 0 ? (
          <div className="tw-p-8 tw-text-center tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-sub-text">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="tw-w-10 tw-h-10 tw-text-gray-300 tw-mb-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <p className="tw-text-xs tw-font-semibold tw-text-slate-600 tw-m-0">
              No conversations found
            </p>
            <p className="tw-text-[11px] tw-text-sub-text tw-mt-1 tw-m-0">
              {activeAccount?.phone ? `No chats for ${activeAccount.phone}` : "No chats for this number"}
            </p>
          </div>
        ) : (
          processedChats.map((chat) => (
            <div
              key={chat.id}
              onClick={() => onSelectChat(chat.id)}
              className={`tw-mx-2 tw-mb-1.5 tw-px-3 tw-py-3 tw-rounded-xl tw-cursor-pointer tw-transition-colors hover:tw-bg-gray-50 ${activeChatId === chat.id ? "tw-bg-[#E1ECFE]" : ""}`}
            >
              <div className="tw-flex tw-gap-3">
                <UserInitialsAvatar
                  name={chat.name}
                  phone={chat.phone}
                  src={chat.avatar}
                  size={34}
                  className="tw-mt-0.5 tw-flex-shrink-0 tw-shadow-sm"
                />
                <div className="tw-flex-1 tw-min-w-0">
                  <div className="tw-flex tw-justify-between tw-items-center tw-mb-1 tw-gap-1">
                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0 tw-flex-wrap sm:tw-flex-nowrap">
                      <h4 className="tw-text-sm tw-font-semibold tw-text-text-primary tw-m-0 tw-truncate">
                        {chat.name}
                      </h4>
                      {chat.isPinned && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#25D366"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="tw-flex-shrink-0"
                        >
                          <line x1="12" y1="17" x2="12" y2="22"></line>
                          <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"></path>
                        </svg>
                      )}
                      {chat.status && (
                        <span
                          className={`tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[9px] tw-font-semibold tw-whitespace-nowrap ${
                            chat.status === "Active"
                              ? "tw-bg-[#E8F7EE] tw-text-[#25D366]"
                              : chat.status === "Awaiting Reply"
                                ? "tw-bg-[#FFFBE6] tw-text-[#D48806]"
                                : chat.status === "Re-engage"
                                  ? "tw-bg-[#FFF1F0] tw-text-[#FF4D4F]"
                                  : "tw-bg-gray-100 tw-text-gray-600"
                          }`}
                        >
                          {chat.status}
                        </span>
                      )}
                    </div>
                    <span className="tw-text-[10px] tw-text-sub-text tw-whitespace-nowrap">
                      {chat.time}
                    </span>
                  </div>
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                    <p
                      className="tw-text-xs tw-m-0 tw-truncate tw-text-sub-text"
                      title={chat.message}
                    >
                      {(() => {
                        if (!chat.message) {
                          return (
                            <span className="tw-italic tw-text-gray-400">
                              No messages yet
                            </span>
                          );
                        }
                        let text = chat.message;
                        if (
                          typeof text === "string" &&
                          text.trim().startsWith("{")
                        ) {
                          try {
                            const parsed = JSON.parse(text);
                            text =
                              parsed.body ||
                              parsed.header ||
                              parsed.text ||
                              text;
                          } catch (e) {}
                        }
                        return text;
                      })()}
                    </p>
                    {chat.unreadCount > 0 && (
                      <div className="tw-w-4 tw-h-4 tw-bg-[#25D366] tw-rounded-full tw-text-white tw-flex tw-items-center tw-justify-center tw-text-[9px] tw-font-bold tw-flex-shrink-0">
                        {chat.unreadCount}
                      </div>
                    )}
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-[10px] tw-text-sub-text tw-mt-2.5">
                    <UserOutlined className="tw-text-[9px]" />
                    <span>
                      {chat.lastMessageSender ||
                        (chat.message ? chat.name || "Customer" : "No messages yet")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Modal
        title={
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-text-primary tw-font-semibold tw-text-lg">
            <PlusCircleOutlined className="tw-text-[#25D366]" />
            <span>Connect New WhatsApp Business Number</span>
          </div>
        }
        open={isConnectModalOpen}
        onCancel={() => setIsConnectModalOpen(false)}
        footer={null}
        centered
        width={480}
        className="connect-whatsapp-modal"
      >
        <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-4">
          <p className="tw-text-xs tw-text-sub-text tw-m-0">
            Link a new WhatsApp Business number to BeyondLeadz. You can manage
            multiple numbers, view unified statistics, and route conversations
            to different departments.
          </p>

          <div className="tw-flex tw-flex-col tw-gap-3">
            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <label className="tw-text-xs tw-font-semibold tw-text-text-primary">
                Account / Resort Name
              </label>
              <Input
                placeholder="e.g. Goa Sands Resort, Hotel Mumbai"
                value={newAccountName}
                onChange={(e) => setNewAccountName(e.target.value)}
                className="tw-h-10 tw-rounded-lg tw-border-border-secondary"
              />
            </div>

            <div className="tw-flex tw-flex-col tw-gap-1.5">
              <label className="tw-text-xs tw-font-semibold tw-text-text-primary">
                WhatsApp Business Phone Number
              </label>
              <Input
                placeholder="e.g. +91 98765 43210"
                value={newAccountPhone}
                onChange={(e) => setNewAccountPhone(e.target.value)}
                className="tw-h-10 tw-rounded-lg tw-border-border-secondary"
              />
            </div>
          </div>

          <div className="tw-bg-[#F8FAFC] tw-rounded-xl tw-p-3.5 tw-border tw-border-solid tw-border-slate-100">
            <span className="tw-text-[11px] tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wider tw-block tw-mb-2">
              Features Included
            </span>
            <div className="tw-grid tw-grid-cols-2 tw-gap-2.5">
              <div className="tw-flex tw-items-center tw-gap-1.5">
                <span className="tw-text-xs">🤖</span>
                <span className="tw-text-xs tw-font-medium tw-text-slate-700">
                  AI Auto-Responder
                </span>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1.5">
                <span className="tw-text-xs">👥</span>
                <span className="tw-text-xs tw-font-medium tw-text-slate-700">
                  Multi-Agent Inbox
                </span>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1.5">
                <span className="tw-text-xs">📊</span>
                <span className="tw-text-xs tw-font-medium tw-text-slate-700">
                  Detailed Analytics
                </span>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1.5">
                <span className="tw-text-xs">✉️</span>
                <span className="tw-text-xs tw-font-medium tw-text-slate-700">
                  Templates Approval
                </span>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-2">
            <Button
              onClick={() => setIsConnectModalOpen(false)}
              className="tw-h-10 tw-rounded-lg tw-px-5 tw-border-border-secondary tw-text-sub-text"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleAddNewAccount}
              className="tw-h-10 tw-rounded-lg tw-px-5 tw-bg-[#25D366] hover:tw-bg-[#20b857] tw-border-none tw-font-medium tw-text-white"
            >
              Connect Number
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ChatListSidebar;
